import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { message, Spin, Card, Typography, Button } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { DynamicForm } from '../../components/DynamicForm';
import { apiService } from '../../services/api';

const { Text } = Typography;

interface ProjectType {
  id: number;
  name: string;
}

interface Project {
  id: number;
  pid: string;
  client?: {
    name: string;
    contactName?: string;
    contactFirstName?: string;
    contactLastName?: string;
  };
  projectTypes?: ProjectType[];
  customProjectType?: string;
}

export const ProjectRetestForm: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchProject = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getProjectById(projectId!);
      setProject(response.data);
    } catch (error) {
      message.error('Failed to load project information');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    // Try to get project from navigation state first, fallback to API fetch
    const projectFromState = location.state?.project;
    if (projectFromState) {
      setProject(projectFromState);
      setLoading(false);
    } else if (projectId) {
      fetchProject();
    }
  }, [projectId, location.state, fetchProject]);

  const handleSubmit = async (formData: any) => {
    try {
      // Create a new project as a retest
      const retestData = {
        ...formData,
        isRetest: true,
        parentProject: project?.id,
        projectTypes: project?.projectTypes?.map(pt => pt.id),
        customProjectType: project?.customProjectType,
        clientName: project?.client?.contactFirstName && project?.client?.contactLastName 
          ? `${project.client.contactFirstName} ${project.client.contactLastName}`
          : project?.client?.contactName,
        clientCompany: project?.client?.name,
        status: 'proposal'
      };

      await apiService.submitRetest(retestData);
    } catch (error) {
      throw error; // Re-throw to let DynamicForm handle the error
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Loading project information...</Text>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <Card>
        <Text type="danger">Project not found</Text>
      </Card>
    );
  }

  return (
    <div>
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
          <Button 
            onClick={() => navigate('/projects')}
            icon={<ArrowLeftOutlined />}
          >
            Back to Projects
          </Button>
        </div>
      </Card>
      <DynamicForm
        projectId={projectId!}
        projectType={project.projectTypes?.[0]?.name || project.customProjectType || 'default'}
        contentType="requirement-checklist" // Reuse checklist schema for retest
        onSubmit={handleSubmit}
        submitEndpoint="/projects"
        title="Retest Request"
        description={`Project: ${project.client?.name} - ${project.pid}`}
      />
    </div>
  );
};
